// Brand styling for Ghost's Sodo Search modal.
//
// Sodo renders the search popup inside a SAME-ORIGIN iframe (its own
// document), so the theme's page-level CSS can't reach it. Instead we
// inject a <style> straight into the iframe's document. Selectors track
// Sodo's Tailwind utility markup (verified against sodo-search@~1.8) — a
// future Sodo release could rename them and need a re-tweak. Fonts don't
// cross the iframe boundary either, so we re-declare @font-face with
// absolute URLs.
//
// The design (Figma 832:399 desktop / 871:549 mobile) turns Sodo's centred
// white card into a full-bleed Dune overlay: a large PP Monument Extended
// query line with no placeholder and no magnifier, a full-width hairline
// divider, a top-right ✕ to close, and a results list of Monument titles
// over JetBrains Mono excerpts. Sodo already autofocuses the input on open,
// so the cursor is active immediately.

export default function searchModal() {
    const origin = window.location.origin;
    const STYLE_ID = "tc-search-style";

    // ✕ glyph for the close button — two diagonal strokes, alpha-masked so
    // the button's background-color drives the colour (espresso → coral on hover).
    const xMask =
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 21 21'%3E%3Cpath d='M1 1L20 20M20 1L1 20' stroke='black' stroke-width='1.6'/%3E%3C/svg%3E\")";

    const css = `
        @font-face {
            font-family: "PP Monument Extended";
            src: url("${origin}/assets/fonts/PPMonumentExtended-Regular.woff2") format("woff2");
            font-weight: 400; font-style: normal; font-display: swap;
        }
        @font-face {
            font-family: "JetBrains Mono";
            src: url("${origin}/assets/fonts/JetBrainsMono-Regular.woff2") format("woff2");
            font-weight: 400; font-style: normal; font-display: swap;
        }
        @font-face {
            font-family: "JetBrains Mono";
            src: url("${origin}/assets/fonts/JetBrainsMono-Medium.woff2") format("woff2");
            font-weight: 500; font-style: normal; font-display: swap;
        }

        html, body { background: #e8e0d5 !important; }

        /* Full-screen container (h-screen w-screen pt-20) → Dune, top-anchored. */
        .ghost-display {
            padding-top: 0 !important;
            background: #e8e0d5 !important;
        }

        /* Sodo's centred card → full-bleed Dune surface, square, no chrome. */
        .bg-white.rounded-lg.shadow-xl {
            background: #e8e0d5 !important;
            width: 100% !important;
            max-width: none !important;
            min-height: 100vh !important;
            margin: 0 !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            animation: none !important;
        }

        /* Query row — Dune, square, full-width hairline divider beneath. The
           input sits in the upper portion (align-start + padding-top) with the
           divider pushed down by min-height, matching the Figma spacing. */
        .bg-white.flex.items-center {
            position: relative !important;
            align-items: flex-start !important;
            background: #e8e0d5 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            border-bottom: 1px solid rgba(30, 22, 18, .22) !important;
            min-height: 116px !important;
            padding: 33px 66px 0 66px !important;
        }

        /* Drop Sodo's leading magnifier / inline clear-✕ — design shows neither. */
        .justify-center.w-4.h-4.me-3 { display: none !important; }

        /* The query itself — PP Monument Extended, espresso, no placeholder. */
        input {
            font-family: "PP Monument Extended", "Impact", sans-serif !important;
            font-size: 48px !important;
            line-height: 1.1 !important;
            color: #1e1612 !important;
            background: transparent !important;
            border: 0 !important;
            caret-color: #1e1612 !important;
            margin-inline-start: 0 !important;
            padding-inline-start: 0 !important;
            padding-right: 60px !important;
        }
        input::placeholder { color: transparent !important; }

        /* Sodo's mobile-only "Cancel" closes the popup — promote it to the
           always-visible top-right ✕ from the design. */
        button.text-neutral-500 {
            display: block !important;
            position: absolute !important;
            top: 49px !important;
            right: 66px !important;
            margin: 0 !important;
            width: 21px !important;
            height: 21px !important;
            font-size: 0 !important;
            background-color: #1e1612 !important;
            -webkit-mask: center / contain no-repeat ${xMask};
            mask: center / contain no-repeat ${xMask};
            cursor: pointer;
            transition: background-color 200ms ease;
        }
        button.text-neutral-500:hover { background-color: #f4714a !important; }

        /* Results — fill the remaining height. */
        .overflow-y-auto {
            max-height: calc(100vh - 116px) !important;
        }

        /* Group wrappers (POSTS / TAGS / AUTHORS): drop the top rules and the
           group labels, align content to the query gutter. */
        .border-t.py-3 {
            border-top: 0 !important;
            padding: 28px 66px 8px 66px !important;
        }
        .overflow-y-auto h1 { display: none !important; }

        /* Result rows — roomier vertical rhythm than Sodo's tight py-3. */
        .overflow-y-auto > div > div { padding-top: 16px !important; padding-bottom: 16px !important; }

        /* Result title — PP Monument Extended, espresso. */
        .overflow-y-auto h2 {
            font-family: "PP Monument Extended", "Impact", sans-serif !important;
            font-size: 40px !important;
            line-height: 1.05 !important;
            color: #1e1612 !important;
            margin-bottom: 8px !important;
        }

        /* Result excerpt — JetBrains Mono, espresso. */
        .overflow-y-auto p {
            font-family: "JetBrains Mono", monospace !important;
            font-size: 16px !important;
            line-height: 1.5 !important;
            color: rgba(30, 22, 18, .85) !important;
        }

        /* Hovered / keyboard-selected result — coral tint. */
        .bg-neutral-100 { background: rgba(244, 113, 74, .14) !important; }

        /* Author avatar initial. */
        .bg-neutral-200 { background: #1e1612 !important; color: #e8e0d5 !important; }

        /* "No matches found" empty state. */
        .py-4.px-7 { padding-left: 66px !important; padding-right: 66px !important; }
        .py-4.px-7 p {
            font-family: "JetBrains Mono", monospace !important;
            font-size: 16px !important;
            color: rgba(30, 22, 18, .6) !important;
        }

        /* Mobile (Figma 871:549): tighter gutters, smaller type, ✕ at top-right. */
        @media (max-width: 639px) {
            .bg-white.flex.items-center {
                min-height: 126px !important;
                padding: 40px 18px 0 18px !important;
            }
            /* Tall leading (Figma 871:578: 20px text on an 80px line box) drops
               the glyph's visual centre to ≈80px — top 40 + half the 80px box.
               This sits the query low in the 126px block and lines it up with the
               ✕ (centre ≈81px), matching the design's vertical rhythm. */
            input { font-size: 20px !important; line-height: 80px !important; padding-right: 44px !important; }
            button.text-neutral-500 { top: 71px !important; right: 18px !important; }
            .overflow-y-auto { max-height: calc(100vh - 126px) !important; }
            .border-t.py-3 { padding-left: 18px !important; padding-right: 18px !important; }
            .py-4.px-7 { padding-left: 18px !important; padding-right: 18px !important; }
            .overflow-y-auto h2 { font-size: 20px !important; }
        }
    `;

    // Sodo creates the iframe and then REWRITES its document (injecting its
    // own main.css), which wipes any style added too early. So we poll and
    // re-inject whenever our <style> is missing from the *current* document —
    // this survives the rewrite and a close/reopen (which makes a new iframe).
    function inject() {
        const iframe = document.querySelector("#sodo-search-root iframe");
        if (!iframe) return;
        let doc;
        try {
            doc = iframe.contentDocument;
        } catch (e) {
            return; // cross-origin guard (shouldn't happen — Sodo's iframe is same-origin)
        }
        if (!doc || !doc.body || doc.getElementById(STYLE_ID)) return;
        const style = doc.createElement("style");
        style.id = STYLE_ID;
        style.textContent = css;
        (doc.head || doc.documentElement).appendChild(style);
    }

    setInterval(inject, 200);
}
